import React, { useEffect, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import * as echarts from "echarts";
import { POE_THRESHOLD_PERCENT, RSSI_CATEGORIES, CHART_TOOLTIP_STYLE } from "../const";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.greyPrimary,
  fontWeight: theme.typography.customFontWeight[0],
  padding: "10px 16px",
  borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
}));

const RssiChart = ({ wirelessClientRssi = [] }) => {
  const theme = useTheme();
  const chartRef = useRef(null);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom || wirelessClientRssi.length === 0) return;

    const chart = echarts.init(chartDom);
    const days = wirelessClientRssi.map((_, idx) => `D${idx + 1}`);

    const weakData = wirelessClientRssi.map((item) => item?.weak ?? 0);
    const fairData = wirelessClientRssi.map((item) => item?.fair ?? 0);
    const goodData = wirelessClientRssi.map((item) => item?.good ?? 0);

    const option = {
      grid: {
        top: 20,
        bottom: 30,
        left: 20,
        right: 15,
        containLabel: true,
      },
      legend: {
        bottom: 0,
        left: 20,
        icon: "circle",
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {
          color: "#707ca8",
          fontSize: 11,
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
        },
        backgroundColor: "#fff",
        borderColor: "#e0e4f0",
        borderWidth: 1,
        textStyle: {
          color: "#333",
          fontSize: 12,
        },
        padding: [8, 12],
        extraCssText: CHART_TOOLTIP_STYLE,
      },
      xAxis: {
        type: "category",
        data: days,
        axisLine: {
          lineStyle: {
            color: "#e0e4f0",
          },
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
        },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        /* name: "Clients",
        nameTextStyle: {
          color: "#707ca8",
          fontSize: 11,
          align: "left",
          padding: [0, 0, 8, 0],
        }, */
        axisLine: {
          show: false,
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
        },
        splitLine: {
          lineStyle: {
            color: "#f0f2f8",
          },
        },
      },
      series: [
        {
          name: RSSI_CATEGORIES[0],
          type: "line",
          stack: "rssi",
          smooth: true,
          showSymbol: false,
          areaStyle: {
            opacity: 0.15,
          },
          emphasis: {
            focus: "series",
          },
          itemStyle: {
            color: theme.palette.primary.main
          },
          data: weakData,
        },
        {
          name: RSSI_CATEGORIES[1],
          type: "line",
          stack: "rssi",
          smooth: true,
          showSymbol: false,
          areaStyle: {
            opacity: 0.15,
          },
          emphasis: {
            focus: "series",
          },
          itemStyle: {
            color: theme.palette.primary.blueSecound
          },
          data: fairData,
        },
        {
          name: RSSI_CATEGORIES[2],
          type: "line",
          stack: "rssi",
          smooth: true,
          showSymbol: false,
          areaStyle: {
            opacity: 0.15,
          },
          emphasis: {
            focus: "series",
          },
          itemStyle: {
            color: theme.palette.primary.subBlue,
          },
          data: goodData,
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [wirelessClientRssi]);

  if (wirelessClientRssi.length === 0) {
    return (
      <Card
        sx={{
          p: 2.5,
          borderRadius: "8px",
          border: "1px solid #e0e4f0",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: "260px",
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "#172264", fontWeight: 600, mb: 2 }}>
          Wireless Client by RSSI
        </Typography>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "#707ca8", fontSize: "14px" }}>No Data</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        p: 2.5,
        borderRadius: "8px",
        border: "1px solid #e0e4f0",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          color: "#172264",
          fontWeight: 600,
          mb: 2,
        }}
      >
        Wireless Client by RSSI
      </Typography>
      <Box sx={{ width: "100%", height: "200px" }} ref={chartRef} />
    </Card>
  );
};

const PoePowerChart = ({ poePowerConsumption = {} }) => {
  const chartRef = useRef(null);
  const budgetUsagePercent = poePowerConsumption.budgetUsagePercent || [];

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom || budgetUsagePercent.length === 0) return;

    const chart = echarts.init(chartDom);
    const days = budgetUsagePercent.map((_, idx) => `D${idx + 1}`);

    const option = {
      grid: {
        top: 25,
        bottom: 30,
        left: 20,
        right: 20,
        containLabel: true,
      },
      legend: {
        bottom: 0,
        left: 20,
        icon: "rect",
        itemWidth: 10,
        itemHeight: 2,
        textStyle: {
          color: "#707ca8",
          fontSize: 11,
        },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: "#fff",
        borderColor: "#e0e4f0",
        borderWidth: 1,
        textStyle: {
          color: "#333",
          fontSize: 12,
        },
        padding: [8, 12],
        extraCssText: CHART_TOOLTIP_STYLE,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: days,
        axisLine: {
          lineStyle: {
            color: "#e0e4f0",
          },
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: 100,
        axisLine: {
          show: false,
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
          formatter: "{value}%",
        },
        splitLine: {
          lineStyle: {
            color: "#f0f2f8",
          },
        },
      },
      series: [
        {
          name: "PoE Budget Usage (%)",
          type: "line",
          smooth: true,
          showSymbol: false,
          itemStyle: {
            color: "#172664",
          },
          lineStyle: {
            width: 2,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(23, 38, 100, 0.08)" },
              { offset: 1, color: "rgba(23, 38, 100, 0)" }
            ])
          },
          markLine: {
            silent: true,
            symbol: "none",
            data: [
              {
                yAxis: POE_THRESHOLD_PERCENT,
                lineStyle: {
                  color: "#f75555",
                  type: "dashed",
                  width: 1.5,
                },
                label: {
                  position: "end",
                  formatter: `${POE_THRESHOLD_PERCENT}% Threshold (Risk)`,
                  color: "#f75555",
                  fontSize: 10,
                  fontWeight: "bold",
                },
              },
            ],
          },
          data: budgetUsagePercent,
        },
        {
          name: `${POE_THRESHOLD_PERCENT}% Threshold (Risk)`,
          type: "line",
          data: [],
          lineStyle: {
            color: "#f75555",
            type: "dashed",
            width: 1.5,
          },
          itemStyle: {
            color: "#f75555",
          },
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [budgetUsagePercent]);

  if (budgetUsagePercent.length === 0) {
    return (
      <Card
        sx={{
          p: 2.5,
          borderRadius: "8px",
          border: "1px solid #e0e4f0",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: "260px",
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "#172264", fontWeight: 600, mb: 2 }}>
          PoE Power Consumption
        </Typography>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "#707ca8", fontSize: "14px" }}>No Data</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        p: 2.5,
        borderRadius: "8px",
        border: "1px solid #e0e4f0",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          color: "#172264",
          fontWeight: 600,
          mb: 2,
        }}
      >
        PoE Power Consumption
      </Typography>
      <Box sx={{ width: "100%", height: "200px" }} ref={chartRef} />
    </Card>
  );
};

const PoeChangesTable = ({ poePowerChanges = [] }) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        p: 2.5,
        borderRadius: "8px",
        border: "1px solid #e0e4f0",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          color: "#172264",
          fontWeight: 600,
          mb: 2,
        }}
      >
        Changes of PoE Power Consumption (Top 10 &gt; 25%)
      </Typography>
      <TableContainer component={Paper} sx={{ boxShadow: "none", border: "none", borderRadius: 0, bgcolor: "transparent" }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ bgcolor: "transparent" }}>
            <TableRow>
              <TableCell sx={{ fontSize: theme.typography.customFontSize[2], color: theme.palette.primary.main, fontWeight: theme.typography.customFontWeight[3], borderBottom: `1px solid ${theme.palette.primary.greyThread}`, padding: "10px 16px" }}>
                Device Name
              </TableCell>
              <TableCell sx={{ fontSize: theme.typography.customFontSize[2], color: theme.palette.primary.main, fontWeight: theme.typography.customFontWeight[3], borderBottom: `1px solid ${theme.palette.primary.greyThread}`, padding: "10px 16px" }}>
                Previous
              </TableCell>
              <TableCell sx={{ fontSize: theme.typography.customFontSize[2], color: theme.palette.primary.main, fontWeight: theme.typography.customFontWeight[3], borderBottom: `1px solid ${theme.palette.primary.greyThread}`, padding: "10px 16px" }}>
                Now
              </TableCell>
              <TableCell sx={{ fontSize: theme.typography.customFontSize[2], color: theme.palette.primary.main, fontWeight: theme.typography.customFontWeight[3], borderBottom: `1px solid ${theme.palette.primary.greyThread}`, padding: "10px 16px" }}>
                Change
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {poePowerChanges.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ color: theme.palette.primary.greyThird, py: 4, borderBottom: `1px solid ${theme.palette.primary.greyThread}` }}>
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              poePowerChanges.map((row, idx) => {
                const previousWatts = +Number(row.previousWatts ?? 0).toFixed(2);
                const currentWatts = +Number(row.currentWatts ?? 0).toFixed(2);
                const changePercent = +Number(row.changePercent ?? 0).toFixed(2);
                const isIncrease = changePercent > 0;
                const changeText = isIncrease ? `+${changePercent}%` : `${changePercent}%`;
                const changeColor = isIncrease ? theme.palette.primary.redPrimary : theme.palette.primary.greenPrimary;

                return (
                  <TableRow key={idx}>
                    <StyledTableCell>{row.switchName || "-"}</StyledTableCell>
                    <StyledTableCell>{previousWatts} W</StyledTableCell>
                    <StyledTableCell>{currentWatts} W</StyledTableCell>
                    <StyledTableCell sx={{ color: changeColor, fontWeight: theme.typography.customFontWeight[3] }}>
                      {changeText}
                    </StyledTableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

const RssiPowerAndTable = ({ chartsData = {}, poePowerChanges = [] }) => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <RssiChart wirelessClientRssi={chartsData.wirelessClientRssi || []} />
      </Grid>
      <Grid item xs={12} md={6}>
        <PoePowerChart poePowerConsumption={chartsData.poePowerConsumption || {}} />
      </Grid>
      <Grid item xs={12} md={6}>
        <PoeChangesTable poePowerChanges={poePowerChanges} />
      </Grid>
    </Grid>
  );
};

export default RssiPowerAndTable;
